import React from "react";
import { Box, Container, Heading, Text, Link, VStack } from "@chakra-ui/react";
import Header from "./Header";

const Privacy: React.FC = () => {
    const primaryColor = "#1d4e4e";
    const secondaryColor = "#d3591c";
    const accentColor = "#19be3a";
    const white = "#ffffff";

    return (
        <Box>
            <Header />
            <Box bg="gray.50" minH="100vh" pt="5rem" pb={8}>
                <Container maxW="1200px">
                    <VStack gap={8} align="stretch">
                        <Box
                            bg={primaryColor}
                            p={10}
                            borderRadius="lg"
                            textAlign="center"
                            color={white}
                        >
                            <Heading as="h1" size="2xl" mb={4}>
                                Privacy Policy
                            </Heading>
                            <Text fontSize="xl">Your privacy is important to us</Text>
                        </Box>
                        <Box
                            bg="white"
                            p={8}
                            borderRadius="10px"
                            boxShadow="0 4px 8px rgba(0, 0, 0, 0.1)"
                        >
                            <VStack gap={6} align="stretch">
                                <Heading as="h1" color={primaryColor} textAlign="center" mb={4}>
                                    Privacy Policy for Grade-R
                                </Heading>
                                <Text>
                                    <strong>Last Updated:</strong> May 18, 2025
                                </Text>
                                <Text>
                                    This Privacy Policy describes how Shedeur Apps ("we," "us," or
                                    "our") collects, uses, and shares personal information of users
                                    ("you" or "users") of the Grade-R mobile application (the
                                    "App").
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    1. Information We Collect
                                </Heading>
                                <Text>We collect the following types of information:</Text>
                                <Box
                                    as="ul"
                                    style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}
                                >
                                    <Box as="li" mb={3}>
                                        <strong>a. Account Information:</strong> When you create an
                                        account in the App, we may collect information such as your
                                        name, email address, and any other information you
                                        voluntarily provide.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>b. Grading Data:</strong> The primary function of
                                        our App involves processing and storing grading-related data
                                        that you input. This may include:
                                        <Box
                                            as="ul"
                                            style={{
                                                listStyleType: "circle",
                                                paddingLeft: "1.5rem",
                                                marginTop: "0.5rem",
                                            }}
                                        >
                                            <Box as="li" mb={2}>
                                                Student ID or identifiers
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Course titles
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Grades or scores
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Feedback or notes you provide
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Rubrics or grading criteria
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>c. Usage Data:</strong> We may collect information
                                        about how you use the App, such as the features you access,
                                        the time and duration of your usage, and any errors you
                                        encounter.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>d. Device Information:</strong> We may collect
                                        certain information about your device, including the device
                                        model, operating system, and unique device identifiers.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>e. Payment Information:</strong> If you choose to
                                        upgrade to a paid version of Grade-R, we may collect
                                        information related to the transaction. However, please note
                                        that we primarily utilize third-party payment processors
                                        (particularly, PayStack - a Stripe company) to handle these
                                        transactions. As such, we may receive limited information
                                        from these providers, such as transaction IDs, purchase
                                        dates, and subscription status. We do not directly collect
                                        or store your complete payment details (e.g., credit card
                                        numbers).
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>
                                            f. No Collection of Sensitive Personal Information:
                                        </strong>{" "}
                                        We do not intentionally collect sensitive personal
                                        information such as health information, financial details,
                                        or social security numbers through the App. Please refrain
                                        from entering such information, except for payment
                                        information as described in section 1(e).
                                    </Box>
                                </Box>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    2. How We Use Your Information
                                </Heading>
                                <Text>
                                    We may use the information we collect for the following
                                    purposes:
                                </Text>
                                <Box
                                    as="ul"
                                    style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}
                                >
                                    <Box as="li" mb={3}>
                                        <strong>a. To Provide and Maintain the App:</strong> To
                                        enable you to create an account, input grading data, and
                                        utilize the features of Grade-R.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>b. To Improve the App:</strong> To understand how
                                        users interact with Grade-R, identify areas for improvement,
                                        and develop new features.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>c. To Communicate with You:</strong> To respond to
                                        your inquiries, provide support, and send you important
                                        notices or updates related to Grade-R.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>d. To Process Payments:</strong> To facilitate and
                                        manage payments for upgraded versions of Grade-R, including
                                        verifying transaction details and subscription status.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>e. For Aggregated and Anonymized Data:</strong> We
                                        may aggregate and anonymize user data for statistical or
                                        research purposes. This data will not identify you
                                        individually.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>f. To Ensure Security:</strong> To monitor and
                                        protect the security and integrity of Grade-R.
                                    </Box>
                                </Box>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    3. How We Share Your Information
                                </Heading>
                                <Text>
                                    We will not share your personal information with third parties
                                    except in the following limited circumstances:
                                </Text>
                                <Box
                                    as="ul"
                                    style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}
                                >
                                    <Box as="li" mb={3}>
                                        <strong>a. With Your Consent:</strong> We may share your
                                        information with third parties when we have your explicit
                                        consent to do so.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>b. Service Providers:</strong> We may engage
                                        third-party service providers to assist us with tasks such
                                        as hosting, data analysis, and customer support. These
                                        providers will have access to your information only to the
                                        extent necessary to perform these services on our behalf and
                                        are obligated to protect your information.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>c. Payment Processors:</strong> We may share
                                        transaction information with our third-party payment
                                        processors to facilitate and track payments. These
                                        processors are responsible for the security of your payment
                                        information and are governed by their own privacy policies.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>d. Legal Compliance:</strong> We may disclose your
                                        information if required to do so by law, court order, or
                                        other legal process, or if we believe in good faith that
                                        such disclosure is necessary to protect our rights,
                                        property, or safety, or the rights, property, or safety of
                                        others.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>e. Business Transfers:</strong> In the event of a
                                        merger, acquisition, or sale of all or a portion of our
                                        assets, your information may be transferred as part of the
                                        transaction. We will notify you via email and/or a prominent
                                        notice in Grade-R of any change in ownership or uses of your
                                        personal information.
                                    </Box>
                                </Box>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    4. Data Retention
                                </Heading>
                                <Text>
                                    We will retain your personal information for as long as your
                                    account is active or as needed to provide you with Grade-R's
                                    services. For payment-related information, we will retain it as
                                    long as necessary to provide you with the paid services and to
                                    comply with our legal and accounting obligations. We may also
                                    retain and use your information as necessary to comply with our
                                    legal obligations, resolve disputes, and enforce our agreements.
                                    When your account is terminated, we may retain aggregated and
                                    anonymized data indefinitely.
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    5. Your Rights
                                </Heading>
                                <Text>
                                    Depending on your location, you may have certain rights
                                    regarding your personal information, such as the right to:
                                </Text>
                                <Box
                                    as="ul"
                                    style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}
                                >
                                    <Box as="li" mb={3}>
                                        <strong>a. Access:</strong> Request access to the personal
                                        information we hold about you.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>b. Correction:</strong> Request that we correct any
                                        inaccurate or incomplete personal information.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>c. Deletion:</strong> Request the deletion of your
                                        personal information.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>d. Objection to Processing:</strong> Object to the
                                        processing of your personal information in certain
                                        circumstances.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>e. Data Portability:</strong> Request to receive
                                        your personal information in a structured, commonly used,
                                        and machine-readable format.
                                    </Box>
                                </Box>
                                <Text>
                                    To exercise these rights, please contact us at{" "}
                                    <Link href="mailto:shedeurapps@gmail.com" color={accentColor}>
                                        shedeurapps@gmail.com
                                    </Link>
                                    . We will respond to your request in accordance with applicable
                                    law.
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    6. Security
                                </Heading>
                                <Text>
                                    We take reasonable measures to protect your personal information
                                    from unauthorized access, use, disclosure, alteration, or
                                    destruction. These measures include encryption, secure servers,
                                    regular backups among others. However, please be aware that no
                                    method of transmission over the internet or method of electronic
                                    storage is completely secure, and we cannot guarantee absolute
                                    security.
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    7. Children's Privacy
                                </Heading>
                                <Text>
                                    Grade-R is not intended for children under the age of 16. We do
                                    not knowingly collect personal information from children under
                                    this age. If we become aware that we have collected personal
                                    information from a child under 16 without verifiable parental
                                    consent, we will take steps to delete such information as soon
                                    as possible.
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    8. Changes to This Privacy Policy
                                </Heading>
                                <Text>
                                    We may update this Privacy Policy from time to time to reflect
                                    changes in our practices or applicable laws. We will notify you
                                    of any material changes by posting the updated Privacy Policy
                                    within Grade-R or by other means of communication. Your
                                    continued use of Grade-R after the effective date of the revised
                                    Privacy Policy constitutes your acceptance of the changes.
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    9. Indemnification
                                </Heading>
                                <Text>
                                    You agree to indemnify and hold Shedeur Apps and its officers,
                                    directors, employees, and agents harmless from any and all
                                    claims, liabilities, damages, losses, costs, and expenses,
                                    including reasonable attorneys' fees, arising out of or in any
                                    way connected with your use of Grade-R or your violation of this
                                    Privacy Policy.
                                </Text>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    10. Contact Us
                                </Heading>
                                <Text>
                                    If you have any questions or concerns about this Privacy Policy
                                    or our data practices, please contact us at:
                                </Text>
                                <Box bg="gray.50" p={5} borderRadius="5px" mt={4}>
                                    <Text>Shedeur Apps</Text>
                                    <Link href="mailto:shedeurapps@gmail.com" color={accentColor}>
                                        shedeurapps@gmail.com
                                    </Link>
                                </Box>

                                <Heading as="h2" color={secondaryColor} size="lg" mt={8}>
                                    11. Account Deletion
                                </Heading>
                                <Text>
                                    You have the right to request the deletion of your account and
                                    associated data at any time. Here's what you need to know about
                                    account deletion:
                                </Text>
                                <Box
                                    as="ul"
                                    style={{ listStyleType: "disc", paddingLeft: "1.5rem" }}
                                >
                                    <Box as="li" mb={3}>
                                        <strong>a. How to Delete Your Account:</strong> You can
                                        delete your account by:
                                        <Box
                                            as="ul"
                                            style={{
                                                listStyleType: "circle",
                                                paddingLeft: "1.5rem",
                                                marginTop: "0.5rem",
                                            }}
                                        >
                                            <Box as="li" mb={2}>
                                                Using the account deletion option in the App
                                                settings
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Contacting us directly at shedeurapps@gmail.com
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>b. What Gets Deleted:</strong> When you delete your
                                        account, we will:
                                        <Box
                                            as="ul"
                                            style={{
                                                listStyleType: "circle",
                                                paddingLeft: "1.5rem",
                                                marginTop: "0.5rem",
                                            }}
                                        >
                                            <Box as="li" mb={2}>
                                                Remove your personal information from our active
                                                databases
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Delete your grading data and associated files
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Cancel any active subscriptions
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>c. Data Retention After Deletion:</strong> Some
                                        information may be retained for a limited period for legal,
                                        security, or business purposes, including:
                                        <Box
                                            as="ul"
                                            style={{
                                                listStyleType: "circle",
                                                paddingLeft: "1.5rem",
                                                marginTop: "0.5rem",
                                            }}
                                        >
                                            <Box as="li" mb={2}>
                                                Records of transactions for tax and accounting
                                                purposes
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Information required to prevent fraud or abuse
                                            </Box>
                                            <Box as="li" mb={2}>
                                                Data necessary to comply with legal obligations
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>d. Backup Data:</strong> Deleted information may
                                        persist in our backup systems for up to 30 days before being
                                        permanently removed.
                                    </Box>
                                    <Box as="li" mb={3}>
                                        <strong>e. Effect on Third-Party Services:</strong> Deleting
                                        your account will not affect any data you have shared with
                                        third-party services or other users of the App.
                                    </Box>
                                </Box>
                                <Text mt={4}>
                                    To request account deletion or if you have any questions about
                                    the deletion process, please contact us at{" "}
                                    <Link href="mailto:shedeurapps@gmail.com" color={accentColor}>
                                        shedeurapps@gmail.com
                                    </Link>
                                    .
                                </Text>
                            </VStack>
                        </Box>
                    </VStack>
                </Container>
            </Box>
        </Box>
    );
};

export default Privacy;
